import { initializePaddle, getPaddleInstance, type Paddle, type CheckoutOpenOptions } from '@paddle/paddle-js'

const VENDOR_ID = import.meta.env.VITE_PADDLE_VENDOR_ID
const ENVIRONMENT = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox'

// Track initialization state
let isInitializing = false
let isInitialized = false
let initError: Error | null = null

/**
 * Initialize Paddle instance
 */
export async function initPaddle(): Promise<Paddle | undefined> {
  if (isInitialized) {
    return getPaddleInstance()
  }

  if (isInitializing) {
    // Wait for ongoing initialization
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (isInitialized) {
          clearInterval(checkInterval)
          resolve(getPaddleInstance())
        } else if (initError) {
          clearInterval(checkInterval)
          reject(initError)
        }
      }, 100)
    })
  }

  if (!VENDOR_ID) {
    throw new Error('VITE_PADDLE_VENDOR_ID environment variable is required')
  }

  isInitializing = true
  initError = null

  try {
    // Try to parse vendor ID as number (seller), otherwise use as token
    const sellerId = Number(VENDOR_ID)
    const isNumeric = !isNaN(sellerId) && isFinite(sellerId)

    const paddleInstance = await initializePaddle({
      environment: ENVIRONMENT === 'production' ? 'production' : 'sandbox',
      ...(isNumeric ? { seller: sellerId } : { token: VENDOR_ID }),
    })

    isInitialized = true
    isInitializing = false
    return paddleInstance
  } catch (error) {
    isInitializing = false
    initError = error instanceof Error ? error : new Error('Failed to initialize Paddle')
    throw initError
  }
}

/**
 * Get Paddle instance (must be initialized first)
 */
export function getPaddle(): Paddle | undefined {
  return getPaddleInstance()
}

/**
 * Open Paddle checkout overlay with the specified price ID and optional custom data
 */
export function openCheckout(
  priceId: string,
  options?: {
    customData?: Record<string, unknown>
    customerEmail?: string
    organizationName?: string
  }
): void {
  if (!priceId) {
    throw new Error('Price ID is required to open checkout')
  }

  const paddle = getPaddleInstance()

  if (!paddle) {
    throw new Error('Paddle is not initialized. Call initPaddle() first.')
  }

  // Build success URL on the same hostname - redirect to checkout success page first
  const successUrl = `${window.location.origin}/organization/billing/checkout-success`

  const checkoutOptions: CheckoutOpenOptions = {
    items: [{ priceId, quantity: 1 }],
    settings: {
      variant: 'one-page',
      successUrl,
    },
    ...(options?.customData && { customData: options.customData }),
    ...(options?.customerEmail && {
      customer: {
        email: options.customerEmail,
        ...(options?.organizationName && {
          business: {
            name: options.organizationName,
            taxIdentifier: '',
          },
        }),
      },
    }),
  }

  paddle.Checkout.open(checkoutOptions)
}

